import { RoutineType } from '../graphql';

type Question = Record<string, any> & { questionId: string };

function getChanges(
  original: Question[] | { confirmedValue?: string },
  edited: Question[] | { confirmedValue?: string },
  typeRoutine: RoutineType
) {
  if (
    typeRoutine === RoutineType.QuickConfirm &&
    !Array.isArray(original) &&
    !Array.isArray(edited) &&
    original?.confirmedValue !== edited?.confirmedValue
  ) {
    return [
      {
        oldValue: original?.confirmedValue,
        newValue: edited.confirmedValue,
      },
    ];
  }

  const changes: {
    questionId: string;
    changes: Record<string, { oldValue: any; newValue: any }>;
  }[] = [];
  for (const originalItem of original) {
    const hasExcludedProperty = excludeRoutine.some((prop) =>
      Object.prototype.hasOwnProperty.call(originalItem, prop)
    );

    if (hasExcludedProperty) continue;

    const editedItem = edited.find(
      (e) => e.questionId === originalItem.questionId
    );
    if (!editedItem) continue;

    let hasChanges = false;
    const diff: Record<string, { oldValue: any; newValue: any }> = {};

    for (const key of new Set([
      ...Object.keys(originalItem),
      ...Object.keys(editedItem),
    ])) {
      if (excludeKeysFromDiff.includes(key) || key === 'exceptionCount') {
        continue;
      }

      const oldVal = originalItem[key];
      const newVal = editedItem[key];

      if (key === 'exceptionCount') {
        continue;
      }

      const isEqual =
        Array.isArray(oldVal) && Array.isArray(newVal)
          ? JSON.stringify(oldVal) === JSON.stringify(newVal)
          : oldVal === newVal;

      if (!isEqual) {
        diff[key] = { oldValue: oldVal, newValue: newVal };
        hasChanges = true;
      }
    }

    if (hasChanges) {
      changes.push({ questionId: originalItem.questionId, changes: diff });
    }
  }

  return changes;
}

const excludeRoutine = ['counterType', 'status'];
const excludeKeysFromDiff = ['i18n', 'translations', '__typename'];

const cleanUpOriginalData = (
  original: Question[] | { confirmedValue?: string },
  edited: Question[] | { confirmedValue?: string }
): Question[] | { confirmedValue?: string } => {
  if (!Array.isArray(original) || !Array.isArray(edited)) {
    return original;
  }

  const result: Question[] = [];

  for (const editedQuestion of edited) {
    const originalQuestion = original.find(
      (q) => q.questionId === editedQuestion.questionId
    );

    if (!originalQuestion) {
      result.push(editedQuestion);
      continue;
    }

    if ('counterType' in editedQuestion || 'status' in editedQuestion) {
      result.push({ ...originalQuestion });
      continue;
    }

    result.push(editedQuestion);
  }

  return result;
};

export const cleanUpDataChanges = (
  original: Question[] | { confirmedValue?: string },
  edited: Question[] | { confirmedValue?: string },
  typeRoutine: RoutineType,
  userId: string
) => {
  const dataOriginal = cleanUpOriginalData(original, edited);

  const changeLogs = getChanges(original, edited, typeRoutine);

  const data = Array.isArray(dataOriginal)
    ? { responses: dataOriginal }
    : dataOriginal;

  return {
    ...data,
    auditLog: {
      auditLog: changeLogs,
      userId,
    },
  };
};
